import { Box, Button, Paper, Typography, useTheme } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { getStaffFacingActivityTitle } from "encamp-shared/src/activity/activityDisplayNames";
import { CohortStatus, WorkflowStatus } from "generated-graphql/graphql";
import { useEffect, useState } from "react";
import { Outlet, useMatch, useNavigate } from "react-router-dom";

import { SplitCohortButton } from "../../../../components/Cohort/SplitCohortButton";
import { SplitCohortDialog } from "../../../../components/Cohort/SplitCohortDialog";
import { WorkflowActivityStepper } from "../../../../components/WorkflowActivityStepper";
import { ConductCohortButton } from "../ConductCohortButton";
import { useCohortDetails } from "../useCohortDetails";
import { useConductCohort } from "../useConductCohort";

export function Activities() {
  const theme = useTheme();
  const { data } = useCohortDetails();
  const { conductCohort, loading: conductingCohort } = useConductCohort();
  const [splitCohortModalOpen, setSplitCohortModalOpen] = useState(false);

  const currentWorkflow = data?.cohort?.currentWorkflow;
  const currentActivityId = data?.cohort?.currentWorkflow?.currentActivityId;
  const cohort = data?.cohort;

  // Determine if the conduct cohort button should be shown
  const shouldShowConductButton =
    currentWorkflow &&
    currentWorkflow.status !== WorkflowStatus.Completed &&
    currentWorkflow.status !== WorkflowStatus.Canceled;

  const shouldAutoNavigateToCurrentActivity = useMatch("/staff/cohorts/:cohortId/activities");
  const navigate = useNavigate();
  const alerts = useAlerts();

  useEffect(() => {
    if (shouldAutoNavigateToCurrentActivity && currentActivityId) {
      navigate(currentActivityId, { replace: true });
    }
  }, [currentActivityId, navigate, shouldAutoNavigateToCurrentActivity]);

  const handleConductCohort = () => {
    if (cohort) {
      conductCohort(cohort.id);
    }
  };

  const handleSplitCohort = () => {
    setSplitCohortModalOpen(true);
  };

  const handleCloseSplitModal = () => {
    setSplitCohortModalOpen(false);
  };

  const handleSplitSuccess = () => {
    // Refetch the cohort data to show updated state
    window.location.reload();
  };

  // Empty state for OPEN cohorts without workflow
  const EmptyStateFallback = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
        padding: theme.spacing(4),
        textAlign: "center",
      }}
    >
      <Paper
        elevation={0}
        sx={{
          padding: theme.spacing(4),
          maxWidth: "600px",
          width: "100%",
          borderRadius: 2,
        }}
      >
        <Typography variant="h6" fontWeight="600" sx={{ mb: 2, color: theme.palette.text.primary }}>
          No active workflow
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6 }}>
          This cohort is still open and waiting for all members to be ready. The workflow will not
          begin until all cohort members are in a ready state.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
          You can split the cohort to close it with the ready members and create a new cohort for
          the remaining members.
        </Typography>
        <SplitCohortButton
          onClick={handleSplitCohort}
          cohortStatus={cohort?.status ?? CohortStatus.Open}
          members={cohort?.members ?? []}
          sx={{ minWidth: "160px" }}
        />
      </Paper>
    </Box>
  );

  // No workflow found state (different from empty state)
  const NoWorkflowFallback = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
        padding: theme.spacing(4),
        textAlign: "center",
      }}
    >
      <Paper
        elevation={0}
        sx={{
          padding: theme.spacing(4),
          maxWidth: "600px",
          width: "100%",
          borderRadius: 2,
        }}
      >
        <Typography variant="h6" fontWeight="600" sx={{ mb: 2, color: theme.palette.text.primary }}>
          No active workflow
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
          This cohort is still open and waiting for all members to be ready. The workflow will not
          begin until all cohort members are in a ready state.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
          You can split the cohort to close it with the ready members and create a new cohort for
          the remaining members.
        </Typography>
        <SplitCohortButton
          onClick={handleSplitCohort}
          cohortStatus={cohort?.status ?? CohortStatus.Open}
          members={cohort?.members ?? []}
          sx={{ minWidth: "160px" }}
        />
      </Paper>
    </Box>
  );

  return (
    <>
      <Box component={Paper} elevation={0} sx={{ minHeight: "32rem" }}>
        {!currentWorkflow ? (
          cohort?.status === CohortStatus.Open ? (
            EmptyStateFallback
          ) : (
            NoWorkflowFallback
          )
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 2fr",
              height: "100%",
            }}
          >
            <Box
              sx={{
                borderRight: `solid 1px ${theme.palette.divider}`,
                overflowX: "hidden",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Box flex={1} sx={{ overflowY: "auto" }}>
                <Box sx={{ padding: theme.spacing(2) }}>
                  <Typography variant="h5" sx={{ color: "gray" }}>
                    Cohort Workflow
                  </Typography>
                </Box>
                <WorkflowActivityStepper
                  activities={data?.cohort?.currentWorkflow?.activities ?? []}
                  currentActivityId={data?.cohort?.currentWorkflow?.currentActivityId}
                  showTooltips={false}
                  showChampionIcon={false}
                  showSecondaryText={false}
                  showEmptyState={true}
                  activityTitleFunction={getStaffFacingActivityTitle}
                  maxHeight="none"
                  replace={false}
                  emptyStateMessage="No active workflow activities"
                />
              </Box>
              {/* Conduct Cohort Button - only show if workflow is not completed or canceled */}
              {shouldShowConductButton && (
                <Box
                  sx={{
                    padding: theme.spacing(2),
                    borderTop: `solid 1px ${theme.palette.divider}`,
                  }}
                >
                  <ConductCohortButton
                    onClick={handleConductCohort}
                    loading={conductingCohort}
                    variant="contained"
                    fullWidth
                  />
                </Box>
              )}
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Outlet />
            </Box>
          </Box>
        )}
      </Box>

      {cohort && (
        <SplitCohortDialog
          open={splitCohortModalOpen}
          onClose={handleCloseSplitModal}
          cohortId={cohort.id}
          members={cohort.members || []}
          onSuccess={handleSplitSuccess}
        />
      )}
    </>
  );
}
